import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import apiCustom from "../../utils/ApiCustom";
import { toast } from "react-toastify";
import { HomeIcon } from "@heroicons/react/24/outline";
import ArticleBox from "../../components/client/ArticleBox";

const getDateOnlyTime = (value) => {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
};

const DetailPost = () => {
  const { slug } = useParams();
  const posts = useSelector((state) => state.posts.posts);

  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [commentLoading, setCommentLoading] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const [relatedPostCount, setRelatedPostCount] = useState(3);
  const mainContentRef = useRef(null);

  const currentUserId = Number(localStorage.getItem("userId"));
  const isLoggedIn = !!currentUserId;

  useEffect(() => {
    window.scrollTo(0, 0);
    setArticle(null);
    setComments([]);
    setRelatedPostCount(3);
    setHasSubmitted(false);

    const post = posts?.find(
      (p) => p.slug === slug && p.status === "published",
    );

    if (!post) {
      setError("Không tìm thấy bài viết");
      setLoading(false);
      return;
    }

    const fetchArticle = async () => {
      try {
        const response = await apiCustom.get(`posts-public/${post.id}`);
        setArticle(response.data.data);
        setComments(response.data.data.comments || []);
        setLoading(false);
      } catch (err) {
        setError("Lỗi khi tải bài viết");
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug, posts]);

  useEffect(() => {
    if (!article) return;

    const timeoutId = setTimeout(() => {
      requestAnimationFrame(() => {
        if (mainContentRef.current) {
          const height = mainContentRef.current.offsetHeight;
          const count = Math.floor(height / 400);
          setRelatedPostCount(count > 0 ? count : 1);
        }
      });
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [article, comments]);

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setCommentLoading(true);
    try {
      const response = await apiCustom.post(
        "comment-create",
        {
          content: newComment,
          postId: article.id,
          userId: currentUserId,
        },
        { headers: { "Content-Type": "application/json" } },
      );

      toast(
        response.data.message ||
          "Gửi góp ý thành công. Góp ý sẽ hiện sau khi được duyệt.",
        {
          type: "success",
        },
      );

      setHasSubmitted(true);
      setNewComment("");
    } catch (err) {
      toast.error("Gửi góp ý thất bại");
    } finally {
      setCommentLoading(false);
    }
  };

  if (loading) return <div className="container mx-auto px-4">Đang tải...</div>;
  if (error) return <div className="container mx-auto px-4">Lỗi: {error}</div>;

  const relatedPosts =
    posts?.filter(
      (p) =>
        p.status === "published" &&
        p.slug !== slug &&
        p.category?.id === article?.category?.id,
    ) || [];

  const displayedPosts = relatedPosts.slice(0, relatedPostCount);

  const userHasCommented = comments?.some((c) => {
    const uid = Number(c.user?.id ?? c.userId);
    return uid && currentUserId && uid === currentUserId;
  });

  // ====== Logic sự kiện / notification ======
  const notification = article?.notification || null;
  const isEventPost = !!notification;

  let isEventOver = false;
  let registerLabel = "Đăng ký hoạt động";
  let registerDisabled = false;

  if (isEventPost) {
    const today = getDateOnlyTime(new Date());
    const eventStart = notification.eventStartTime
      ? getDateOnlyTime(notification.eventStartTime)
      : NaN;
    const regisStart = notification.regisStartTime
      ? getDateOnlyTime(notification.regisStartTime)
      : NaN;
    const regisEnd = notification.regisEndTime
      ? getDateOnlyTime(notification.regisEndTime)
      : NaN;

    if (Number.isFinite(eventStart) && today > eventStart) {
      isEventOver = true;
    }

    if (Number.isFinite(regisEnd) && today > regisEnd) {
      // Hết hạn đăng ký
      registerLabel = "Hết hạn đăng ký";
      registerDisabled = true;
    } else if (Number.isFinite(regisStart) && today < regisStart) {
      // Chưa mở đăng ký
      registerLabel = "Chưa mở đăng ký";
      registerDisabled = true;
    } else {
      // Trong thời gian đăng ký
      registerLabel = "Đăng ký hoạt động";
      registerDisabled = false;
    }
  }

  const baseCanShowForm = isLoggedIn && !userHasCommented && !hasSubmitted;

  // Nếu có sự kiện -> chỉ cho góp ý khi sự kiện đã diễn ra
  const canShowForm = isEventPost
    ? baseCanShowForm && isEventOver
    : baseCanShowForm;

  return (
    <div className="container mx-auto px-4">
      <nav className="flex text-gray-500 text-sm mb-4" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-2 flex-wrap">
          <li className="inline-flex items-center">
            <Link
              to="/"
              className="inline-flex items-center hover:text-blue-600 font-medium"
            >
              <HomeIcon className="w-4 h-4 mr-2 text-gray-400" />
              Trang chủ
            </Link>
          </li>
          <li>
            <div className="flex items-center">
              <svg
                className="w-3 h-3 mx-2 text-gray-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M7.05 4.05a.75.75 0 011.06 0l5.25 5.25a.75.75 0 010 1.06l-5.25 5.25a.75.75 0 11-1.06-1.06L11.19 10 7.05 5.91a.75.75 0 010-1.06z" />
              </svg>
              <span className="ml-1 text-gray-400 font-medium">
                {article?.category?.name || "Tin tức"}
              </span>
            </div>
          </li>
          <li>
            <div className="flex items-center">
              <svg
                className="w-3 h-3 mx-2 text-gray-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M7.05 4.05a.75.75 0 011.06 0l5.25 5.25a.75.75 0 010 1.06l-5.25 5.25a.75.75 0 11-1.06-1.06L11.19 10 7.05 5.91a.75.75 0 010-1.06z" />
              </svg>
              <span className="ml-1 text-gray-400 font-medium truncate max-w-[200px]">
                {article?.title}
              </span>
            </div>
          </li>
        </ol>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        <div
          className={
            article?.category?.slug == "chinh-sach"
              ? "lg:col-span-4"
              : "lg:col-span-3"
          }
          ref={mainContentRef}
        >
          {/* Tiêu đề + nút đăng ký */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <h1 className="text-[24px] font-bold">{article?.title}</h1>

            {isEventPost &&
              notification?.slug &&
              (registerDisabled ? (
                <button
                  type="button"
                  disabled
                  className="inline-flex items-center justify-center px-4 py-2 rounded-md text-sm font-semibold text-white bg-gray-400 cursor-not-allowed whitespace-nowrap"
                >
                  {registerLabel}
                </button>
              ) : (
                <Link
                  to={`/thong-bao/${notification?.category?.slug}/${notification.slug}`}
                  className="inline-flex items-center justify-center px-4 py-2 rounded-md text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 whitespace-nowrap"
                >
                  {registerLabel}
                </Link>
              ))}
          </div>

          <div className="text-gray-600 mb-6">
            {new Date(article?.createdAt).toLocaleDateString("vi-VN")}
          </div>

          <div
            className="prose max-w-none break-words rich-content"
            dangerouslySetInnerHTML={{ __html: article?.content }}
          />

          {article?.category?.isComment && (
            <div className="mt-8">
              <h2 className="text-2xl font-semibold mb-4">Góp ý</h2>

              {comments.length > 0 ? (
                <div className="space-y-4">
                  {comments.map((c) => (
                    <div key={c.id} className="bg-gray-100 p-4 rounded-lg">
                      <div className="flex items-center mb-2">
                        <span className="font-medium text-gray-800">
                          {c.user?.userFullName || "Ẩn danh"}
                        </span>
                        <span className="text-gray-500 text-sm ml-2">
                          {new Date(c.createdAt).toLocaleString("vi-VN")}
                        </span>
                      </div>
                      <p className="text-gray-700">{c.content}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">Chưa có góp ý nào.</p>
              )}

              {/* Chưa đăng nhập → nút dẫn tới /login */}
              {!isLoggedIn && (
                <div className="mt-6">
                  <Link
                    to="/login"
                    className="px-4 py-2 rounded-md text-white font-semibold bg-blue-500 hover:bg-blue-600 inline-block"
                  >
                    Đăng nhập để gửi góp ý
                  </Link>
                </div>
              )}

              {/* Nếu là bài gắn sự kiện nhưng sự kiện chưa diễn ra → không cho góp ý, chỉ hiển thị thông báo */}
              {isEventPost && !isEventOver && (
                <p className="mt-4 text-sm text-gray-600">
                  Bạn có thể gửi góp ý sau khi sự kiện đã diễn ra.
                </p>
              )}

              {/* Đã đăng nhập & đủ điều kiện → hiện form */}
              {canShowForm && (
                <form onSubmit={handleCommentSubmit} className="mt-6">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    className="w-full p-2 border rounded-md mb-4 min-h[100px] min-h-[100px]"
                    placeholder="Nhập góp ý của bạn..."
                    required
                  />
                  <button
                    type="submit"
                    disabled={commentLoading}
                    className={`px-4 py-2 rounded-md text-white font-semibold ${
                      commentLoading
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-blue-500 hover:bg-blue-600"
                    }`}
                  >
                    {commentLoading ? "Đang gửi..." : "Gửi góp ý"}
                  </button>
                </form>
              )}

              {/* Nếu đã gửi ở phiên hiện tại (đang chờ duyệt) → ẩn form */}
              {isLoggedIn && hasSubmitted && !userHasCommented && (
                <p className="mt-4 text-sm text-gray-600">
                  Góp ý của bạn đã được gửi và sẽ hiển thị sau khi được duyệt.
                </p>
              )}
            </div>
          )}
        </div>

        {article?.category?.slug != "chinh-sach" && (
          <div className="lg:col-span-1 space-y-4 overflow-y-auto">
            <div className="text-xl font-semibold text-black border-b-2 border-blue-500 pb-2 mb-4">
              Bài viết cùng danh mục
            </div>

            {displayedPosts.map((post) => (
              <ArticleBox key={post.id} article={post} />
            ))}

            {relatedPosts.length > relatedPostCount && (
              <Link
                to={`/hoat-dong/${article?.category?.slug || "unknown"}`}
                className="text-right inline-block mt-2 text-blue-500 hover:underline text-base w-full"
              >
                Xem thêm bài viết khác →
              </Link>
            )}
          </div>
        )}
      </div>

      <style>{`
        .rich-content,
        .rich-content * {
          box-sizing: border-box;
        }

        .rich-content p:not([style*="text-align"]),
        .rich-content li:not([style*="text-align"]),
        .rich-content div:not([style*="text-align"]),
        .rich-content blockquote:not([style*="text-align"]) {
          text-align: justify !important;
        }

        /* Giữ các kiểu căn lề do editor sinh ra */
        .rich-content p[style*="text-align"],
        .rich-content li[style*="text-align"],
        .rich-content div[style*="text-align"],
        .rich-content blockquote[style*="text-align"] {
          text-align: inherit;
        }

        .rich-content img,
        .rich-content video,
        .rich-content svg,
        .rich-content canvas {
          max-width: 100% !important;
          height: auto !important;
          display: block;
          margin-left: auto;
          margin-right: auto;
        }

        .rich-content iframe {
          width: 100% !important;
          max-width: 100% !important;
        }

        .rich-content table {
          display: block;
          width: 100% !important;
          max-width: 100% !important;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          border-collapse: collapse;
          border-spacing: 0;
        }

        .rich-content table th,
        .rich-content table td {
          border: 1px solid #d1d5db;
          padding: 8px 10px;
          vertical-align: top;
        }

        .rich-content table th {
          background-color: #f3f4f6;
          font-weight: 600;
        }

        .rich-content pre {
          max-width: 100%;
          overflow-x: auto;
          white-space: pre-wrap;
          word-break: break-word;
        }

        .rich-content a {
          word-break: break-word;
        }

        .rich-content figure {
          margin: 0;
        }

        .rich-content [style*="width"] {
          max-width: 100% !important;
        }
      `}</style>
    </div>
  );
};

export default DetailPost;